import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Select, Tag, Space, notification,
         message, Alert, Tooltip, Popconfirm, Row, Col, Divider, Radio, Tabs, Progress, Empty } from 'antd';
import { MailOutlined, PlusOutlined, DeleteOutlined,
         CopyOutlined, LinkOutlined, UserOutlined, CheckCircleOutlined } from '@ant-design/icons';
import SurveyService from '../../services/SurveyService';
import FacilityService from '../../services/FacilityService';
import util from '../../utils/util';
const { Option } = Select;
const { TabPane } = Tabs;
const REGIONS = ['Hhohho', 'Manzini', 'Lubombo', 'Shiselweni'];

export default function QoCSurveyCollectors({ survey, onClose }) {
    const [collectors,  setCollectors]  = useState([]);
    const [facilities,  setFacilities]  = useState([]);
    const [sections,    setSections]    = useState([]);
    const [loading,     setLoading]     = useState(false);
    const [inviteModal, setInviteModal] = useState(false);
    const [form] = Form.useForm();
    const [filteredFacs, setFilteredFacs] = useState([]);
    const [regionFilter, setRegionFilter] = useState(null);
    const [sectionScope, setSectionScope] = useState('all');

    // ── Section progress view state ──────────────────────────────────────
    const [progressFacility, setProgressFacility] = useState(null);
    const [progress, setProgress]         = useState(null);
    const [progressLoading, setProgressLoading] = useState(false);
    const [markingSubmitted, setMarkingSubmitted] = useState(false);

    useEffect(() => {
        loadCollectors();
        loadFacilities();
        loadSections();
    }, []); // eslint-disable-line

    const loadCollectors = () => {
        setLoading(true);
        SurveyService.getCollectors(survey.id)
            .then(r => setCollectors(r.data?.data || []))
            .catch(() => {})
            .finally(() => setLoading(false));
    };
    const loadFacilities = () => {
        FacilityService.all({ status:1 })
            .then(r => setFacilities(r.data?.result?.data || []))
            .catch(() => {});
    };
    const loadSections = () => {
        SurveyService.getSurveyDetail(survey.id)
            .then(r => setSections(r.data?.data?.sections || []))
            .catch(() => {});
    };

    // Filter facilities by region selection
    useEffect(() => {
        if (regionFilter) {
            setFilteredFacs(facilities.filter(f => f.located_region === regionFilter));
        } else {
            setFilteredFacs(facilities);
        }
    }, [regionFilter, facilities]);

    const invite = async () => {
        try {
            const vals = await form.validateFields();
            util.showLoader();
            const payload = {
                email:         vals.email,
                name:          vals.name || '',
                region_filter: vals.region_filter || '',
                facilities:    vals.facilities || [],
                section_ids:   sectionScope === 'specific' ? (vals.section_ids || []) : [],
            };
            const res = await SurveyService.inviteCollector(survey.id, payload);
            const result = res.data?.data;
            if (result?.email_sent) {
                message.success(`âœ… Email sent to ${vals.email}`);
            } else {
                // Email failed — show link so admin can share manually
                Modal.info({
                    title: 'Collector invited — share link manually',
                    content: (
                        <div>
                            <p>Email delivery failed. Share this link with the collector:</p>
                            <Input.TextArea rows={3} value={result?.link} readOnly
                                style={{fontFamily:'monospace',fontSize:11}}/>
                            <Button style={{marginTop:8}} onClick={()=>{
                                navigator.clipboard.writeText(result?.link);
                                message.success('Link copied');
                            }}>Copy Link</Button>
                        </div>
                    ),
                    width: 520,
                });
            }
            setInviteModal(false);
            form.resetFields();
            setRegionFilter(null);
            setSectionScope('all');
            loadCollectors();
        } catch(e) { message.error(e.response?.data?.message || e.message); }
        finally { util.hideLoader(); }
    };

    const revoke = async (id) => {
        util.showLoader();
        try {
            await SurveyService.removeCollector(id);
            message.success('Access revoked');
            loadCollectors();
        } catch(e) { message.error(e.message); }
        finally { util.hideLoader(); }
    };

    const copyLink = (token) => {
        const base = window.location.hostname.includes('qoctesting')
            ? window.location.origin + '/qoctesting'
            : window.location.origin + '/qoc';
        const link = `${base}/#/survey/collect/${token}`;
        navigator.clipboard.writeText(link);
        message.success('Collector link copied');
    };

    const getLink = (token) => {
        const base = window.location.hostname.includes('qoctesting')
            ? window.location.origin + '/qoctesting'
            : window.location.origin + '/qoc';
        return `${base}/#/survey/collect/${token}`;
    };

    // ── Section progress ─────────────────────────────────────────────────
    const loadProgress = (facilityId) => {
        if (!facilityId) { setProgress(null); return; }
        setProgressLoading(true);
        SurveyService.getSectionProgress(survey.id, facilityId)
            .then(r => setProgress(r.data?.data || null))
            .catch(e => message.error(e.response?.data?.message || e.message))
            .finally(() => setProgressLoading(false));
    };

    const handleMarkSubmitted = () => {
        Modal.confirm({
            title: 'Mark this facility\'s response as fully submitted?',
            content: 'Only do this once every section shows complete below. This is the final step — it cannot be undone from here.',
            okText: 'Mark as Submitted',
            onOk: async () => {
                setMarkingSubmitted(true);
                try {
                    await SurveyService.markSubmitted(survey.id, progressFacility);
                    message.success('Marked as submitted');
                    loadProgress(progressFacility);
                } catch(e) { message.error(e.response?.data?.message || e.message); }
                finally { setMarkingSubmitted(false); }
            },
        });
    };

    const cols = [
        { title: 'Name / Email', render: (_, r) => (
            <div>
                <div style={{fontWeight:700}}>{r.name || r.email}</div>
                <div style={{fontSize:11,color:'#888'}}>{r.email}</div>
            </div>
        )},
        { title: 'Region', dataIndex: 'region_filter', width: 110,
            render: v => v ? <Tag color="blue">{v}</Tag> : <span style={{color:'#ccc'}}>All regions</span>
        },
        { title: 'Facilities', dataIndex: 'facilities', width: 110,
            render: v => {
                const facs = v ? (Array.isArray(v) ? v : JSON.parse(v)) : null;
                return facs?.length ? <Tag color="purple">{facs.length} facilities</Tag> : <span style={{color:'#ccc'}}>All</span>;
            }
        },
        { title: 'Sections', dataIndex: 'section_ids', width: 110,
            render: v => {
                const secs = v ? (Array.isArray(v) ? v : JSON.parse(v)) : null;
                return secs?.length ? <Tag color="gold">{secs.length} section{secs.length!==1?'s':''}</Tag> : <span style={{color:'#ccc'}}>Entire tool</span>;
            }
        },
        { title: 'Status', dataIndex: 'status', width: 90,
            render: v => <Tag color={v==='active'?'green':v==='revoked'?'red':'orange'}>{v}</Tag>
        },
        { title: 'Last Access', dataIndex: 'last_accessed', width: 120,
            render: v => v ? <span style={{fontSize:12}}>{v.split(' ')[0]}</span> : <span style={{color:'#ccc'}}>Never</span>
        },
        { title: '', width: 120, render: (_, r) => (
            <Space size={4}>
                <Tooltip title="Copy collector link">
                    <Button size="small" icon={<CopyOutlined/>} onClick={()=>copyLink(r.access_token)}/>
                </Tooltip>
                <Tooltip title="Open link">
                    <Button size="small" icon={<LinkOutlined/>}
                        href={getLink(r.access_token)} target="_blank"/>
                </Tooltip>
                {r.status === 'active' && (
                    <Popconfirm title="Revoke access?" onConfirm={()=>revoke(r.id)} okButtonProps={{danger:true}}>
                        <Button size="small" danger icon={<DeleteOutlined/>}/>
                    </Popconfirm>
                )}
            </Space>
        )},
    ];

    const anySectionScoped = collectors.some(c => {
        const secs = c.section_ids ? (Array.isArray(c.section_ids) ? c.section_ids : JSON.parse(c.section_ids)) : null;
        return secs && secs.length;
    });

    return (
        <div>
            <Tabs defaultActiveKey="collectors">
                <TabPane tab="Manage Collectors" key="collectors">
                    <Alert type="info" showIcon style={{marginBottom:16}}
                        message="Collector Management"
                        description="Invite data collectors by email. Each gets a unique link — no login required. You can limit which facilities, region, and — for long tools — which sections they can access."/>

                    <div style={{display:'flex',justifyContent:'space-between',marginBottom:12}}>
                        <div style={{fontWeight:700,fontSize:14,color:'#0d2137'}}>
                            {collectors.length} collector{collectors.length!==1?'s':''} invited
                        </div>
                        <Button type="primary" icon={<PlusOutlined/>} onClick={()=>setInviteModal(true)}>
                            Invite Collector
                        </Button>
                    </div>

                    <Table rowKey="id" dataSource={collectors} columns={cols}
                        loading={loading} size="small" pagination={false}
                        locale={{emptyText:'No collectors invited yet'}}/>
                </TabPane>

                <TabPane tab="Section Progress" key="progress">
                    <Alert type="info" showIcon style={{marginBottom:16}}
                        message="Section Progress"
                        description="Pick a facility to see which sections are assigned to whom, and whether each has been captured yet. Once every section is complete, you can mark the response as fully submitted here."/>

                    <Select style={{width:320,marginBottom:16}} placeholder="Select a facility to view progress"
                        showSearch optionFilterProp="children" allowClear
                        value={progressFacility}
                        onChange={v => { setProgressFacility(v); loadProgress(v); }}>
                        {facilities.map(f => <Option key={f.id} value={f.id}>{f.business_name}</Option>)}
                    </Select>

                    {!progressFacility ? (
                        <Empty description="Select a facility above to see its section progress" />
                    ) : progressLoading ? (
                        <div style={{textAlign:'center',padding:30}}>Loading…</div>
                    ) : progress ? (
                        <div>
                            <Table size="small" pagination={false} rowKey="section_id"
                                dataSource={progress.sections || []}
                                columns={[
                                    { title:'Section', dataIndex:'title' },
                                    { title:'Assigned To', dataIndex:'assigned_to',
                                        render: v => v?.length
                                            ? v.map(a => <Tag key={a.email}>{a.name || a.email}</Tag>)
                                            : <span style={{color:'#f5222d'}}>Nobody assigned</span>
                                    },
                                    { title:'Progress', width:180, render:(_,r) => (
                                        <Progress percent={r.total ? Math.round(r.answered/r.total*100) : 0}
                                            size="small" status={r.complete ? 'success' : 'active'}
                                            format={() => `${r.answered}/${r.total}`}/>
                                    )},
                                    { title:'', width:80, render:(_,r) => r.complete
                                        ? <Tag color="green" icon={<CheckCircleOutlined/>}>Complete</Tag>
                                        : <Tag color="orange">In progress</Tag>
                                    },
                                ]}/>
                            <div style={{marginTop:16, display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                                <span style={{fontSize:12,color:'#888'}}>
                                    Response status: <Tag>{progress.response_status || 'not started'}</Tag>
                                </span>
                                {progress.response_status === 'draft' && (
                                    <Button type="primary" icon={<CheckCircleOutlined/>}
                                        loading={markingSubmitted}
                                        disabled={!(progress.sections||[]).every(s => s.complete)}
                                        onClick={handleMarkSubmitted}
                                        style={{background:'#16a34a',borderColor:'#16a34a'}}>
                                        Mark as Submitted
                                    </Button>
                                )}
                            </div>
                        </div>
                    ) : (
                        <Empty description="No response started yet for this facility" />
                    )}
                </TabPane>
            </Tabs>

            {/* Invite Modal */}
            <Modal title={<span><MailOutlined/> Invite Data Collector</span>}
                open={inviteModal} onCancel={()=>{setInviteModal(false);form.resetFields();setRegionFilter(null);setSectionScope('all');}}
                onOk={invite} okText="Send Invitation" width={600}>
                <Form form={form} layout="vertical">
                    <Row gutter={12}>
                        <Col span={14}>
                            <Form.Item name="email" label="Email Address" rules={[{required:true,type:'email',message:'Enter a valid email'}]}>
                                <Input prefix={<MailOutlined/>} placeholder="collector@mohsz.gov.sz"/>
                            </Form.Item>
                        </Col>
                        <Col span={10}>
                            <Form.Item name="name" label="Full Name (optional)">
                                <Input prefix={<UserOutlined/>} placeholder="John Dlamini"/>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Divider style={{margin:'8px 0 16px'}}>Facility Access</Divider>

                    <Alert type="warning" showIcon style={{marginBottom:12}} message="Leave both blank to allow access to all facilities"/>

                    <Form.Item name="region_filter" label="Limit to Region">
                        <Select allowClear placeholder="All regions" onChange={v=>{setRegionFilter(v);form.setFieldValue('facilities',[]);}}>
                            {REGIONS.map(r=><Option key={r} value={r}>{r}</Option>)}
                        </Select>
                    </Form.Item>

                    <Form.Item name="facilities" label={`Limit to Specific Facilities ${regionFilter?'('+regionFilter+')':''}`}>
                        <Select mode="multiple" allowClear showSearch
                            placeholder="Select specific facilities (optional)"
                            filterOption={(input,opt)=>String(Array.isArray(opt.children) ? opt.children.join('') : opt.children).toLowerCase().includes(input.toLowerCase())}
                            maxTagCount={3}>
                            {filteredFacs.map(f=>(
                                <Option key={f.id} value={f.id}>
                                    {f.business_name} — {f.located_region}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    {sections.length > 1 && (
                        <>
                            <Divider style={{margin:'8px 0 16px'}}>Tool Access</Divider>
                            <Form.Item label="What can this collector access?">
                                <Radio.Group value={sectionScope} onChange={e => setSectionScope(e.target.value)}>
                                    <Radio value="all">Entire Tool</Radio>
                                    <Radio value="specific">Specific Sections</Radio>
                                </Radio.Group>
                            </Form.Item>
                            {sectionScope === 'specific' && (
                                <Form.Item name="section_ids" label="Select Section(s)"
                                    rules={[{ required: true, message: 'Select at least one section' }]}>
                                    <Select mode="multiple" allowClear showSearch
                                        placeholder="Select one or more sections"
                                        filterOption={(input,opt)=>String(Array.isArray(opt.children) ? opt.children.join('') : opt.children).toLowerCase().includes(input.toLowerCase())}>
                                        {sections.map(s => (
                                            <Option key={s.id} value={s.id}>{s.title}</Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            )}
                            {sectionScope === 'specific' && anySectionScoped === false && (
                                <Alert type="info" showIcon style={{marginTop:4}}
                                    message="Once any collector is assigned to specific sections, only the survey owner or a co-owner will be able to do the final submission for this tool — individual collectors will save their section but won't submit the whole response themselves."/>
                            )}
                        </>
                    )}

                    <Alert type="info" showIcon style={{marginTop:8}}
                        message="The collector will receive a unique link granting access only to what you select above."/>
                </Form>
            </Modal>
        </div>
    );
}

